import { createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import {
  IngredientsSearchResults,
  INGREDIENT_SEARCH_LIMIT,
} from "@/components/ingredient-db/ingredients-search-results";
import {
  INGREDIENT_CATEGORY_FILTERS,
  INGREDIENT_SOURCE_FILTERS,
} from "@/components/ingredient-db/ingredient-search-params";
import { ContainerSmall } from "@/components/primitives/container-small";
import { PageContainer } from "@/components/primitives/layout";
import { optionalStringSearchParam } from "@/lib/searchParams";
import { maybeGetSessionUser } from "@/lib/serverAuth";
import { searchIngredients } from "@/lib/searchIngredients";

const searchSchema = z.object({
  q: optionalStringSearchParam,
  category: z.enum(INGREDIENT_CATEGORY_FILTERS).optional().catch(undefined),
  source: z.enum(INGREDIENT_SOURCE_FILTERS).optional().catch(undefined),
  page: z.preprocess((value) => {
    const page = Number(value);
    return Number.isSafeInteger(page) && page > 0 ? page : undefined;
  }, z.number().optional()),
});

type IngredientSearch = z.infer<typeof searchSchema>;

const getIngredientSearchData = createServerFn({ method: "GET" })
  .inputValidator((data: IngredientSearch) => data)
  .handler(async ({ data }) => {
    const user = await maybeGetSessionUser();
    const response = await searchIngredients({
      query: data.q,
      category: data.category,
      source: data.source,
      limit: INGREDIENT_SEARCH_LIMIT,
      offset: ((data.page ?? 1) - 1) * INGREDIENT_SEARCH_LIMIT,
      userId: user?.id ?? null,
    });

    return { ...response, userId: user?.id ?? null };
  });

export const Route = createFileRoute("/ingredients/search")({
  validateSearch: searchSchema,
  beforeLoad: ({ location, search }) => {
    const rawPage = new URLSearchParams(location.searchStr).get("page");
    const page = search.page ?? 1;

    if (rawPage !== null && rawPage !== (page > 1 ? String(page) : null)) {
      throw redirect({
        to: "/ingredients/search",
        search: {
          q: search.q,
          category: search.category,
          source: search.source,
          page: page > 1 ? page : undefined,
        },
        replace: true,
      });
    }
  },
  head: () => ({
    meta: [
      { title: "Search Ingredients - Scoopulator" },
      {
        name: "description",
        content:
          "Search and filter ice cream ingredients by category and source.",
      },
    ],
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const response = await getIngredientSearchData({ data: deps });
    const page = deps.page ?? 1;
    const totalPages = Math.max(
      1,
      Math.ceil(response.total / INGREDIENT_SEARCH_LIMIT),
    );

    if (page > totalPages) {
      throw redirect({
        to: "/ingredients/search",
        search: {
          q: deps.q,
          category: deps.category,
          source: deps.source,
          page: totalPages > 1 ? totalPages : undefined,
        },
        replace: true,
      });
    }

    return { ...response, page };
  },
  component: IngredientSearchPage,
});

function IngredientSearchPage() {
  const { data, total, userId, page } = Route.useLoaderData();

  return (
    <PageContainer>
      <ContainerSmall>
        <IngredientsSearchResults
          ingredients={data}
          total={total}
          currentPage={page}
          userId={userId}
        />
      </ContainerSmall>
    </PageContainer>
  );
}
